/**
 * User account lifecycle — soft-delete and restore — as the settings
 * Danger Zone sees it: which action a user's saved state offers, and how
 * the bulk preflight / bulk result bodies reduce to "may proceed" plus the
 * reasons a target was left out.
 *
 * Contains:
 * - LIFECYCLE_ACTIONS — wire action names accepted by users/bulk/
 * - lifecycleActionFor — the action a user's permissions section offers
 * - originalEmail — the address a soft-deleted account restores to
 * - summarizePreflight — proceed flag + blockers from a preflight body
 * - summarizeOutcome — applied flag + blockers from a bulk result body
 */

export const LIFECYCLE_ACTIONS = Object.freeze({ DELETE: 'delete', RESTORE: 'restore' });

const PROCEED_KEY = Object.freeze({
  [LIFECYCLE_ACTIONS.DELETE]: 'to_delete',
  [LIFECYCLE_ACTIONS.RESTORE]: 'to_restore',
});

const APPLIED_KEY = Object.freeze({
  [LIFECYCLE_ACTIONS.DELETE]: 'deleted',
  [LIFECYCLE_ACTIONS.RESTORE]: 'restored',
});

// Buckets that explain a target missing from the proceed/applied list, in
// display order. Every bucket is a list of ids except `self_excluded` (a
// bool) and `failed` (a list of { id, detail }).
export const BLOCKER_CODES = Object.freeze([
  'self_excluded',
  'superuser_excluded',
  'already_deleted',
  'already_active',
  'conflicts',
  'not_found',
  'failed',
]);

const blockerIds = (body, code) => {
  const value = body?.[code];
  if (code === 'self_excluded') return value ? [] : null;
  if (!Array.isArray(value) || value.length === 0) return null;
  return code === 'failed' ? value.map(entry => entry.id) : value;
};

const blockersFor = body =>
  BLOCKER_CODES.flatMap(code => {
    const ids = blockerIds(body, code);
    return ids ? [{ code, ids }] : [];
  });

/**
 * The lifecycle action a user's `permissions` settings section offers.
 *
 * @param {{ is_deleted?: boolean } | null | undefined} permissions
 * @returns {'delete'|'restore'}
 */
export const lifecycleActionFor = permissions =>
  permissions?.is_deleted ? LIFECYCLE_ACTIONS.RESTORE : LIFECYCLE_ACTIONS.DELETE;

/**
 * The address a soft-deleted account returns to on restore. Soft-delete
 * parks the email as `deleted-<id>-<email>`; anything else passes through.
 *
 * @param {string | null | undefined} email
 * @param {number | string} userId
 * @returns {string | null | undefined}
 */
export const originalEmail = (email, userId) => {
  const prefix = `deleted-${userId}-`;
  return typeof email === 'string' && email.startsWith(prefix) ? email.slice(prefix.length) : email;
};

/**
 * Reduce a users/bulk/preflight/ body to a go/no-go plus blockers.
 *
 * @param {'delete'|'restore'} action
 * @param {object | null | undefined} body
 * @returns {{ canProceed: boolean, proceedIds: number[], blockers: Array<{ code: string, ids: number[] }> }}
 */
export const summarizePreflight = (action, body) => {
  const proceedIds = body?.[PROCEED_KEY[action]] ?? [];
  return { canProceed: proceedIds.length > 0, proceedIds, blockers: blockersFor(body) };
};

/**
 * Reduce a users/bulk/ result body to an applied flag plus blockers.
 *
 * @param {'delete'|'restore'} action
 * @param {object | null | undefined} body
 * @returns {{ applied: boolean, appliedIds: number[], blockers: Array<{ code: string, ids: number[] }> }}
 */
export const summarizeOutcome = (action, body) => {
  const appliedIds = body?.[APPLIED_KEY[action]] ?? [];
  return { applied: appliedIds.length > 0, appliedIds, blockers: blockersFor(body) };
};
